import React, {
    FC,
    createContext,
    useState,
    useCallback,
    PropsWithChildren,
    useMemo,
    useEffect,
} from "react";
import {Platform} from "react-native";

import {useErrorReporting} from "@unibuddy/error-reporting";
import {useIsFeatureEnabledBoolean} from "ubcommunity-shared/src/Hooks/useIsFeatureEnabledBoolean";
import {SSO_PROVIDER, SSO_INIT_STATUS} from "ubcommunity-shared/src/Auth/SSO/auth-ui/types";
import {API_URL} from "ubcommunity-shared/src/constants/constants";
import {SSO_FEATURE_FLAG, SSO_FEATURE_FLAG_NATIVE} from "ubcommunity-shared/src/featureFlags";
import removeTrailingSlash from "ubcommunity-shared/src/Utils/remove-trailing-slash";
import {ssoInit} from "./auth-ui";
import {useGetUniSlug} from "./utils/useGetUniSlug";
import {useAsyncLocalStorage} from "../AuthProvider/useAsyncLocalStorage";

const SSO_APPLICATION_ID_WEB = "social-community";
const SSO_APPLICATION_ID_MOBILE = "social-community-mobile";
const ENDPOINT_URL = removeTrailingSlash(API_URL);

type TSSOContext = {
    ssoStatus?: SSO_INIT_STATUS;
    isRedirectUrlMissing?: boolean;
};

const SSOCacheLocalStorageKey = "__UB_TMP_SSO__";

/**
 * SSOContext handles the Social sign-in while init/setup state.
 */
export const SSOContext = createContext<TSSOContext>({
    ssoStatus: SSO_INIT_STATUS.UNKNOWN,
    isRedirectUrlMissing: false,
});

export const SSOProvider: FC<PropsWithChildren> = ({children}) => {
    const socialLoginEnabledFeatureFlag = useIsFeatureEnabledBoolean(
        Platform.OS === "web" ? SSO_FEATURE_FLAG : SSO_FEATURE_FLAG_NATIVE,
    );
    const {reportError} = useErrorReporting();
    const {getUniSlug} = useGetUniSlug();
    const universitySlug = getUniSlug();
    const [ssoStatus, setSSOStatus] = useState<SSO_INIT_STATUS>(SSO_INIT_STATUS.UNKNOWN);
    const {value: ssoValue} = useAsyncLocalStorage(SSOCacheLocalStorageKey, undefined);

    const initialiseSSO = useCallback(async () => {
        setSSOStatus(SSO_INIT_STATUS.LOADING);
        try {
            await ssoInit({
                applicationId:
                    Platform.OS === "web" ? SSO_APPLICATION_ID_WEB : SSO_APPLICATION_ID_MOBILE,
                ssoProviders: [SSO_PROVIDER.GOOGLE, SSO_PROVIDER.APPLE],
                universitySlug: universitySlug,
                gatewayUrl: ENDPOINT_URL,
                successUrl: "/auth/sso/success",
            });
            setSSOStatus(SSO_INIT_STATUS.READY);
        } catch (err) {
            setSSOStatus(SSO_INIT_STATUS.FAILED);
            reportError(new Error(err?.message ?? "SSO Init failed"));
        }
    }, [universitySlug, reportError]);

    const value = useMemo(() => {
        return {
            ssoStatus: ssoStatus,
            isRedirectUrlMissing:
                !!ssoValue &&
                (!ssoValue.serverRedirectUrl_apple || !ssoValue.serverRedirectUrl_google),
        };
    }, [ssoStatus, ssoValue]);

    useEffect(() => {
        if (socialLoginEnabledFeatureFlag && universitySlug) initialiseSSO();
    }, [socialLoginEnabledFeatureFlag, universitySlug, initialiseSSO]);

    return <SSOContext.Provider value={value}>{children}</SSOContext.Provider>;
};
